import * as React from 'react'

import { F0Select } from '@factorialco/f0-react'
import { EntityId, Input, NumberInput } from '@factorialco/f0-react/dist/experimental'

import { VariableSelectorConfig, VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import {
  convertArrayFromWorkflowValue,
  convertArrayToWorkflowValue,
  convertEntityFromWorkflowValue,
  convertToWorkflowValue,
} from 'modules/workflows/components/Sidepanel/utils'
import i18n from 'modules/core/lib/i18n'

import { OperatorEnum, VariableTypeEnum } from './types'

export type VariableData = {
  name?: string
  type: VariableTypeEnum
  options?: string[] | null
  unit?: string | null
}

const isMultiOperator = (operator?: OperatorEnum) => operator === 'one_of' || operator === 'not_one_of'

export const RightHandSelector = ({
  variableName,
  value,
  onChange,
  variablesConfig,
  operator,
  disabled,
  error,
  variableData,
}: {
  variableName: string | undefined
  value: string | undefined
  onChange: (newValue: string) => void
  variablesConfig: VariablesConfig
  operator?: OperatorEnum
  disabled?: boolean
  error?: boolean | string
  variableData?: VariableData
}) => {
  // If no variable is selected, show a placeholder input
  if (!variableName) {
    return (
      <Input
        error={error}
        size="md"
        hideLabel
        label={''}
        value={i18n.t('workflows.sidepanel.condition.paths.value')}
        disabled
        onChange={() => {}}
      />
    )
  }

  if (!variableData) {
    throw new Error(`Variable data not found for: ${variableName}`)
  }

  const selectorConfig: VariableSelectorConfig | undefined = variablesConfig[variableName]
  if (!selectorConfig) {
    throw new Error(`Selector not found in variablesConfig mapping for variable: ${variableName}`)
  }

  // Boolean inputs always render a Yes/No selector (single select).
  if (selectorConfig.type === 'boolean') {
    return (
      <F0Select
        size="md"
        hideLabel
        clearable={false}
        label={i18n.t('workflows.sidepanel.condition.paths.value')}
        options={[
          { value: 'true', label: i18n.t('common.yes') },
          { value: 'false', label: i18n.t('common.no') },
        ]}
        value={value}
        disabled={disabled}
        placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
        onChange={(newValue: string) => onChange(newValue)}
        error={error}
      />
    )
  }

  if (selectorConfig.type === 'number') {
    // NumberInput can emit temporary string values while the user is typing.
    // Only persist numeric-like strings so we never store invalid literals like "dadada".
    const handleNumberChange = (newValue: unknown) => {
      if (newValue === null) {
        onChange('')
        return
      }

      if (typeof newValue === 'number') {
        onChange(convertToWorkflowValue(newValue))
        return
      }

      if (typeof newValue === 'string') {
        if (newValue === '' || /^-?\d*\.?\d*$/.test(newValue)) {
          onChange(newValue)
        }
      }
    }

    const numberValue = value ? (isNaN(parseFloat(value)) ? null : parseFloat(value)) : null
    return (
      <NumberInput
        locale={i18n.locale}
        size="md"
        hideLabel
        disabled={disabled}
        label={i18n.t('workflows.sidepanel.condition.paths.value')}
        value={numberValue}
        onChange={handleNumberChange}
        units={selectorConfig.unit}
        placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
        error={error}
      />
    )
  }

  if (selectorConfig.type === 'entity') {
    // Check if operator is 'one_of' or 'not_one_of' - use multi-select if available
    if (isMultiOperator(operator) && selectorConfig.multiSelect) {
      const MultiEntitySelector = selectorConfig.component.multi
      const entityValues = convertArrayFromWorkflowValue<EntityId>(value || '[]')

      const handleMultiEntityChange = (newEntityValues: EntityId[] | null) => {
        const stringValue = convertArrayToWorkflowValue<EntityId>(newEntityValues, 'number')
        onChange(stringValue)
      }

      return (
        <MultiEntitySelector
          value={entityValues}
          onChange={handleMultiEntityChange}
          disabled={disabled}
          placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
          error={error}
        />
      )
    }

    // Single select (default)
    const EntitySelector = selectorConfig.component.single
    const entityValue = convertEntityFromWorkflowValue(value || '')

    const handleEntityChange = (newEntityValue: EntityId | null) => {
      const stringValue = convertToWorkflowValue(newEntityValue)
      onChange(stringValue)
    }

    return (
      <EntitySelector
        disabled={disabled}
        value={entityValue}
        onChange={handleEntityChange}
        placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
        error={error}
      />
    )
  }

  if (selectorConfig.type === 'text') {
    if (selectorConfig.multiSelect) {
      const isMultiple = isMultiOperator(operator)

      const multiValue = isMultiple
        ? convertArrayFromWorkflowValue<string>(value || '[]')
        : convertEntityFromWorkflowValue(value || '')

      const handleMultiChange = (newValue: string[] | string | null) => {
        if (isMultiple) {
          const stringValue = convertArrayToWorkflowValue<string>(newValue)
          onChange(stringValue)
        } else if (typeof newValue === 'string') {
          const stringValue = convertToWorkflowValue(newValue)
          onChange(stringValue)
        }
      }

      const options = variableData.options?.map((option) => ({ value: option, label: option })) ?? []

      // Key forces re-mount when switching between single/multi mode to clear internal state.
      const selectKey = `${variableName}-${isMultiple ? 'multi' : 'single'}`

      return (
        <F0Select
          key={selectKey}
          size="md"
          hideLabel
          multiple={isMultiple}
          clearable={false}
          label={i18n.t('workflows.sidepanel.condition.paths.value')}
          options={options}
          value={multiValue}
          disabled={disabled}
          placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
          onChange={handleMultiChange}
          error={error}
        />
      )
    }

    return (
      <Input
        size="md"
        hideLabel
        placeholder={i18n.t('workflows.sidepanel.condition.paths.value')}
        label={''}
        value={value}
        disabled={disabled}
        onChange={(newValue) => onChange(newValue)}
        error={error}
      />
    )
  }

  return null
}

